import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Zap } from 'lucide-react';

// --- Sound Effects Hook/Logic using Tone.js ---
// Tone.js needs to be imported or included in your project (e.g., npm install tone)
// If you cannot use npm, you'll need to use simpler HTML5 Audio, but this is better.
// Assuming 'tone' is available globally or imported via 'import * as Tone from "tone";'
import * as Tone from 'tone'; 

const useGameSounds = () => {
    const matchSynth = useRef(null);
    const flipSynth = useRef(null);
    const contextStarted = useRef(false);

    // Initialize Synths once
    useEffect(() => {
        // Simple Synth for Match Sound (deeper, sustained)
        matchSynth.current = new Tone.Synth({
            oscillator: { type: "square" },
            envelope: { attack: 0.005, decay: 0.2, sustain: 0.1, release: 0.5 }
        }).toDestination();

        // PolySynth for Flip/Mismatch Sound (quicker)
        flipSynth.current = new Tone.PolySynth(Tone.Synth).toDestination();

        return () => {
            if (matchSynth.current) matchSynth.current.dispose();
            if (flipSynth.current) flipSynth.current.dispose();
        };
    }, []);

    const startAudioContext = useCallback(() => {
        if (!contextStarted.current) {
            Tone.start();
            contextStarted.current = true;
        }
    }, []);

    const playFlipSound = (isMatch) => {
        if (!contextStarted.current) return;
        if (isMatch) {
            // Match sound (A4)
            matchSynth.current.triggerAttackRelease("A4", "4n"); 
        } else {
            // Flip sound (High C5/E5)
            flipSynth.current.triggerAttackRelease(["C5", "E5"], "16n"); 
        }
    };
    
    const playMismatchSound = () => {
        if (!contextStarted.current) return;
        // Mismatch sound (Low C4)
        flipSynth.current.triggerAttackRelease("C4", "8n"); 
    };

    return { playFlipSound, playMismatchSound, startAudioContext };
};


// --- Game Constants ---
const EMOJIS = ['🚀', '🌟', '🦄', '🌈', '🍦', '🍩', '🎈', '🎨']; // 8 pairs (4x4 grid is easier)
const GRID_SIZE = 4; // 4x4 grid for 8 pairs (16 cards)

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
    let newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
};

// --- Initial State Function ---
const initializeCards = () => {
    let cardValues = [...EMOJIS, ...EMOJIS];
    cardValues = shuffleArray(cardValues);

    return cardValues.map((value, index) => ({
        id: index,
        value: value,
        isFlipped: false,
        isMatched: false,
    }));
};


// --- Main Game Component ---
const EmojiMatchMania = () => {
    const [cards, setCards] = useState(initializeCards);
    const [flippedIndices, setFlippedIndices] = useState([]);
    const [matchedPairs, setMatchedPairs] = useState(0);
    const [isLocked, setIsLocked] = useState(false);
    const [isGameWon, setIsGameWon] = useState(false);
    
    const { playFlipSound, playMismatchSound, startAudioContext } = useGameSounds();

    // Reset the entire game state
    const resetGame = useCallback(() => {
        setCards(initializeCards());
        setFlippedIndices([]);
        setMatchedPairs(0);
        setIsLocked(false);
        setIsGameWon(false);
    }, []);

    // --- Core Game Logic: Check for Match ---
    useEffect(() => {
        if (flippedIndices.length === 2) {
            setIsLocked(true);
            const [index1, index2] = flippedIndices;
            const card1 = cards[index1];
            const card2 = cards[index2];

            const isMatch = card1.value === card2.value;

            if (isMatch) {
                // Handle Match
                setTimeout(() => {
                    setCards(prevCards => 
                        prevCards.map(card => 
                            card.id === index1 || card.id === index2 
                                ? { ...card, isMatched: true, isFlipped: true } // Keep flipped and set matched
                                : card
                        )
                    );
                    setMatchedPairs(prev => prev + 1);
                    setFlippedIndices([]);
                    setIsLocked(false);
                    playFlipSound(true); // Play match sound
                }, 700);

            } else {
                // Handle Mismatch (Flip back)
                setTimeout(() => {
                    setCards(prevCards => 
                        prevCards.map(card => 
                            card.id === index1 || card.id === index2 
                                ? { ...card, isFlipped: false } // Flip back
                                : card
                        )
                    );
                    setFlippedIndices([]);
                    setIsLocked(false);
                    playMismatchSound(); // Play mismatch sound
                }, 1200);
            }
        }
    }, [flippedIndices, cards, playFlipSound, playMismatchSound]);


    // --- Check Win Condition ---
    useEffect(() => {
        if (matchedPairs === EMOJIS.length) {
            setTimeout(() => setIsGameWon(true), 500);
        }
    }, [matchedPairs]);


    // --- Click Handler ---
    const handleCardClick = (index) => {
        startAudioContext(); // Ensure audio context starts on user interaction

        if (isLocked || cards[index].isFlipped || flippedIndices.length === 2) {
            return;
        }

        // 1. Flip the card
        setCards(prevCards => 
            prevCards.map(card => 
                card.id === index ? { ...card, isFlipped: true } : card
            )
        );

        // 2. Add to flipped list
        setFlippedIndices(prev => {
            const newFlipped = [...prev, index];
            if (newFlipped.length === 1) {
                // Only play flip sound on the first card
                playFlipSound(false); 
            }
            return newFlipped;
        });
    };

    // --- Card Component ---
    const Card = ({ card, onClick }) => {
        const cardClasses = `card w-full h-full aspect-square relative transform-style-preserve-3d 
                             transition-transform duration-500 rounded-xl cursor-pointer shadow-lg 
                             ${card.isFlipped ? 'is-flipped' : ''} 
                             ${card.isMatched ? 'opacity-60 pointer-events-none' : ''}`;

        return (
            <div 
                className={cardClasses}
                onClick={onClick}
                // Prevent interaction if already matched or board is locked
                style={{ pointerEvents: card.isMatched || isLocked ? 'none' : 'auto' }}
            >
                {/* Card Back */}
                <div 
                    className="card-face card-back absolute w-full h-full bg-indigo-500 rounded-xl 
                               flex items-center justify-center text-white text-3xl font-extrabold"
                >
                    <Zap className="w-8 h-8" />
                </div>
                
                {/* Card Front */}
                <div 
                    className="card-face card-front absolute w-full h-full bg-yellow-300 rounded-xl 
                               flex items-center justify-center text-gray-900 text-5xl font-extrabold 
                               transform-rotateY-180 border-4 border-yellow-500"
                    style={{ transform: 'rotateY(180deg)' }}
                >
                    {card.value}
                </div>
            </div>
        );
    };

    // --- Game Renderer ---
    return (
        <div className="flex flex-col items-center p-4 min-h-screen bg-pink-100 font-fredoka">
            <style jsx global>{`
                /* Simple component-specific styles for the 3D flip effect */
                .transform-style-preserve-3d {
                    transform-style: preserve-3d;
                }
                .is-flipped {
                    transform: rotateY(180deg);
                }
                .card-face {
                    backface-visibility: hidden;
                }
                .font-fredoka {
                    font-family: 'Fredoka', sans-serif;
                }
            `}</style>
            
            <div className="game-container bg-white p-6 sm:p-8 rounded-[30px] border-8 border-orange-300 shadow-2xl max-w-xl w-full">
                <h1 className="text-4xl text-center text-pink-600 mb-6 font-extrabold">
                    Emoji Match Mania!
                </h1>

                {/* Scoreboard and Controls */}
                <div className="flex justify-between items-center mb-6 px-2">
                    <div className="text-xl text-gray-700">
                        Matches: <span className="font-bold text-indigo-700">{matchedPairs}</span> / {EMOJIS.length}
                    </div>
                    <button 
                        onClick={resetGame} 
                        className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white font-bold rounded-full shadow-md hover:bg-green-600 transition-colors"
                    >
                        <RefreshCw className="w-5 h-5" />
                        Restart
                    </button>
                </div>

                {/* Game Grid (4x4) */}
                <div 
                    className="game-grid grid gap-3" 
                    style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)` }}
                >
                    {cards.map(card => (
                        <Card 
                            key={card.id} 
                            card={card} 
                            onClick={() => handleCardClick(card.id)}
                        />
                    ))}
                </div>
                
                {/* Win Modal */}
                {isGameWon && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4 transition-opacity">
                        <div className="bg-white p-8 rounded-2xl shadow-2xl text-center max-w-sm w-full transform scale-100 transition-transform duration-300">
                            <p className="text-4xl font-extrabold text-pink-600 mb-4">🥳 You Win! 🥳</p>
                            <p className="text-xl font-medium text-gray-600 mb-6">Great job matching all the pairs!</p>
                            <button 
                                onClick={resetGame} 
                                className="px-6 py-3 bg-yellow-500 text-white font-bold rounded-full shadow-lg hover:bg-yellow-600 transition-colors"
                            >
                                Play Again!
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmojiMatchMania;